// src/components/review/ReviewSection.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useReviews } from '../../contexts/ReviewContext';
import StarRating from 'react-stars';
import '../../styles/review/ReviewSection.css';

const ReviewSection = ({ bookId }) => {
  const { currentUser } = useAuth();
  const { getReviews, loadReviews, addReview } = useReviews();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadReviews(bookId).then(setReviews);
  }, [bookId, loadReviews]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return setMessage('Please log in to leave a review.');
    if (!text.trim()) return setMessage('Please write a review.');

    setLoading(true);
    try {
      await addReview(bookId, {
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email.split('@')[0],
        rating,
        text: text.trim(),
      });
      setText('');
      setRating(5);
      setMessage('Thank you! Your review has been submitted.');
      loadReviews(bookId).then(setReviews); // Refresh
    } catch {
      setMessage('Failed to submit review.');
    } finally {
      setLoading(false);
    }
  };

  const reviewCount = reviews.length;
  const avgRating = reviewCount > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviewCount).toFixed(1)
    : null;

  // Star distribution
  const starDist = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: reviews.filter(r => r.rating === star).length,
    percentage: reviewCount ? Math.round((reviews.filter(r => r.rating === star).length / reviewCount) * 100) : 0
  }));

  return (
    <div className="amazon-review-section">
      <h2 className="review-title">Customer Reviews</h2>

      {avgRating ? (
        <div className="review-summary">
          <div className="average-rating">
            <StarRating value={parseFloat(avgRating)} edit={false} size={32} color2="#FFA41C" />
            <div className="avg-text">
              <strong>{avgRating} out of 5</strong>
              <p>{reviewCount} global ratings</p>
            </div>
          </div>

          <div className="star-distribution">
            {starDist.map(({ star, percentage }) => (
              <div key={star} className="dist-row">
                <span className="star-label">{star} star</span>
                <div className="bar-container">
                  <div className="bar-fill" style={{ width: `${percentage}%` }}></div>
                </div>
                <span className="percent">{percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="no-reviews-summary">No reviews yet. Be the first to review this product!</p>
      )}

      {/* Write Review Form */}
      <div className="write-review-form">
        <h3>Write a Review</h3>
        <p className="form-subtitle">Share your thoughts with other customers</p>

        {message && <p className="form-message">{message}</p>}

        <form onSubmit={handleSubmit}>
          <div className="rating-group">
            <label>Your Rating</label>
            <StarRating count={5} value={rating} onChange={setRating} size={36} color2="#FFA41C" />
          </div>

          <div className="text-group">
            <label>Your Review</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Share your thoughts..."
              rows="6"
              required
            />
          </div>

          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? 'Submitting...' : 'Submit Review'}
          </button>
        </form>
      </div>

      {/* Individual Reviews */}
      <div className="individual-reviews">
        <h3>Reviews</h3>
        {reviews.length === 0 ? (
          <p>No reviews yet.</p>
        ) : (
          reviews.map(review => (
            <div key={review.id} className="single-review">
              <div className="review-head">
                <strong>{review.userName}</strong>
                <StarRating value={review.rating} edit={false} size={20} color2="#FFA41C" />
              </div>
              <p className="review-body">{review.text}</p>
              {review.createdAt && (
                <small className="review-date">
                  Reviewed on {review.createdAt.toLocaleDateString()}
                </small>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ReviewSection;